import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useLanguage } from '@/context/LanguageContext';

const LIBRARY_TOUR_KEY = 'cca_library_tour_seen_v1';
const TOUR_LANG_KEY = 'cca_tour_lang';

/**
 * LibraryTour — fires on the /library route. Two trigger modes:
 *   (1) Auto when `localStorage.cca_library_tour_seen_v1 === 'pending'`
 *       (set by FirstVisitTour when user taps "Show me the Library →")
 *   (2) Manually via the "Take the tour" link on /about footer (which
 *       clears the flag back to 'pending' before navigating to /library)
 *
 * Narration language is read from `localStorage.cca_tour_lang` so it
 * matches whichever language the user picked at the start of the
 * FirstVisitTour. Falls back to the site's current language.
 *
 * Walks through:
 *   - Search input
 *   - English Dialects + Spanish Dialects sections (15 + 13 entries)
 *   - "Browse by Category" — each of the 10 main categories with
 *     every subcategory enumerated in the description
 *   - First phrase card (favorite, audio, copy)
 *   - Bottom navigation tabs
 */
const LibraryTour = () => {
  const { pathname } = useLocation();
  const { isSpanish: siteIsSpanish } = useLanguage();
  const fired = useRef(false);
  // Track the active driver.js instance — same removeChild-crash
  // protection as FirstVisitTour.
  const drvRef = useRef(null);

  useEffect(() => {
    if (pathname !== '/library') return;
    if (fired.current) return;

    const state = localStorage.getItem(LIBRARY_TOUR_KEY);
    if (state !== 'pending') return; // already seen or never started

    // Honor the language the user picked at the start of the first-visit
    // tour. If they bypassed that picker (e.g. fired this tour manually
    // from /about), fall back to whatever language the site is in.
    let tourLang;
    try { tourLang = localStorage.getItem(TOUR_LANG_KEY); } catch { tourLang = null; }
    const useEs = tourLang ? tourLang === 'es' : siteIsSpanish;

    let pollTimer = null;
    let watchdog = null;

    // The library page loads phrase cards asynchronously. Wait until
    // either the search input AND at least one category card render
    // OR a short watchdog fires.
    const KEY_TESTIDS = [
      '[data-testid="library-search-input"]',
      '[data-testid="category-chip"]',
    ];
    const allReady = () =>
      KEY_TESTIDS.every((sel) => document.querySelector(sel));

    const startTour = () => {
      // English copy. Each "category-chip" step targets the Nth chip
      // by its position in the DOM (alphabetically sorted in HomePage)
      // and enumerates every subcategory in the description so the
      // user sees the full taxonomy at a glance.
      //
      // Main categories (alphabetical order, must match HomePage.jsx
      // line 139 sort) — May 2026 snapshot from /api/categories:
      //   1. Generational Slang (English)          — 5 subs
      //   2. Generational Slang (Spanish)          — 5 subs
      //   3. Interacting with Others (Casual—EN)   — 6 subs
      //   4. Interacting with Others (Casual—ES)   — 6 subs
      //   5. Interacting with Others (Pro—EN)      — 3 subs
      //   6. Interacting with Others (Pro—ES)      — 3 subs
      //   7. Medical Terminology                    — 10 subs
      //   8. Regional Slang & Phrases (English)    — 15 subs (UI: "English Dialects")
      //   9. Regional Slang & Phrases (Spanish)    — 13 subs (UI: "Spanish Dialects")
      const stepsEn = [
        {
          element: '[data-testid="library-search-input"]',
          popover: { title: '🔍 Search', description: 'Search by keyword, mood, or situation — "running late", "set boundaries", anything.', side: 'bottom', align: 'start' },
        },
        {
          element: '[data-testid="english-dialects-toggle"]',
          popover: {
            title: '🇬🇧 English Dialects',
            description: 'Tap to expand 15 English-speaking dialects: African English, Asian English, Australian Slang, British English, Canadian Slang, Caribbean Slang, Deep South US, East Coast US, Hip Hop & Street Slang, Midwest US, New Zealand Slang, South African, Southern US, Texas Slang, and West Coast US.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-testid="spanish-dialects-toggle"]',
          popover: {
            title: '🇪🇸 Spanish Dialects',
            description: 'Tap to expand 13 Spanish-speaking dialects: California Spanish, Canary Islands Spanish, Central American Spanish, Chavacano, Easter Island Spanish, Equatorial Guinea Spanish, Island Countries\' Phrases, Mexican Spanish, New Mexico Spanish, Puerto Rican Spanish, South American Spanish, Spain Spanish, and Tejas / Spanglish.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(1)',
          popover: { title: '🏷️ Browse by Category', description: 'Below the dialects are 9 themed categories. I\'ll walk you through each one with its subcategories.', side: 'bottom', align: 'center' },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(1)',
          popover: {
            title: '🗣️ Generational Slang (English)',
            description: '5 subcategories: Baby Boomers (1946-1964), Gen X (1965-1979), Gen Y / Millennials (1980-1994), Gen Z (1995-2009), and Gen Alpha (2010-2024).',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(2)',
          popover: {
            title: '🗣️ Generational Slang (Spanish)',
            description: '5 subcategories: Baby Boomers Español, Gen X Español, Millennials Español, Gen Z Español, and Gen Alpha Español.',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(3)',
          popover: {
            title: '💬 Interacting with Others (Casual — English)',
            description: '6 subcategories: Dating & Relationships, Family & Home, Idioms & Expressions, Sarcasm & Humor, Social (General), and Texting & Online.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(4)',
          popover: {
            title: '💬 Interacting with Others (Casual — Spanish)',
            description: '6 subcategories: Dating & Relationships, Family & Home, Idioms & Expressions, Sarcasm & Humor, Social (General), and Texting & Online — all in Spanish.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(5)',
          popover: {
            title: '💼 Interacting with Others (Professional / Errands — English)',
            description: '3 subcategories: Healthcare, Services, and Workplace. Day-to-day errand-running and work talk.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(6)',
          popover: {
            title: '💼 Interacting with Others (Professional / Errands — Spanish)',
            description: '3 subcategories: Healthcare, Services, and Workplace — all in Spanish.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(7)',
          popover: {
            title: '🩺 Medical Terminology',
            description: '10 subcategories: Common Medical Jargon, Medical Abbreviations, Medical Prefixes, Medical Root Words, Medical Suffixes, Dental Jargon, Optical Jargon, Insurance Jargon, Pharmaceutical Jargon, and Surgical Jargon. Decode anything your doctor (or insurer) throws at you.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(8)',
          popover: {
            title: '🌎 Regional Slang & Phrases (English)',
            description: 'The same 15 regional English dialects from the section above — also browsable as a category card.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(9)',
          popover: {
            title: '🌎 Regional Slang & Phrases (Spanish)',
            description: 'The same 13 regional Spanish dialects from the section above — also browsable as a category card.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="phrase-card"]',
          popover: {
            title: '🗨️ Phrase cards',
            description: 'Tap any card to see what it really means, hear it spoken aloud, save it to favorites, and copy it to share.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="phrase-favorite-btn"]',
          popover: { title: '❤️ Save to favorites', description: 'Tap the heart to keep your go-to phrases close. View them anytime in your library.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="phrase-audio-btn"]',
          popover: { title: '🔊 Listen', description: 'Hear the phrase out loud — useful for practicing tone and pacing.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-library"]',
          popover: { title: '📚 Library', description: 'You\'re here right now. This is your home base for phrases.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-todo"]',
          popover: { title: '✅ To-Do', description: 'A neurodivergent-friendly task list with dopamine rewards.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-timer"]',
          popover: { title: '⏲️ Timer', description: 'Pomodoro timer with the Dino Llama duo — focus + breaks.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-interpreter"]',
          popover: {
            title: '✨ Thought Interpreter',
            description: 'Type your own message and Detective Dino rewrites it in your chosen tone and regional dialect — kinder, clearer, and ready to send.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="nav-tab-friends"]',
          popover: { title: '👥 Friends', description: 'Connect with others on the same journey. Encourage each other.', side: 'top', align: 'center' },
        },
        {
          popover: {
            title: '🎉 You\'re all set!',
            description: 'Have a look around, and remember — the "Take the tour" link is on the About page if you ever want a refresher.',
          },
        },
      ];

      // Spanish copy — same structure, translated descriptions. The
      // subcategory names stay in their original form when that's how
      // they appear in the library UI (e.g. "Gen Z Español" is the
      // actual category name in the Spanish data set).
      const stepsEs = [
        {
          element: '[data-testid="library-search-input"]',
          popover: { title: '🔍 Buscar', description: 'Busca por palabra, emoción o situación — "voy tarde", "poner límites", etc.', side: 'bottom', align: 'start' },
        },
        {
          element: '[data-testid="english-dialects-toggle"]',
          popover: {
            title: '🇬🇧 Dialectos del Inglés',
            description: 'Toca para ver 15 dialectos del inglés: African English, Asian English, Australian Slang, British English, Canadian Slang, Caribbean Slang, Deep South US, East Coast US, Hip Hop & Street Slang, Midwest US, New Zealand Slang, South African, Southern US, Texas Slang, y West Coast US.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-testid="spanish-dialects-toggle"]',
          popover: {
            title: '🇪🇸 Dialectos del Español',
            description: 'Toca para ver 13 dialectos del español: Español de California, Español de Canarias, Español Centroamericano, Chavacano, Español de Isla de Pascua, Español de Guinea Ecuatorial, Frases de Países Insulares, Español Mexicano, Español de Nuevo México, Español Puertorriqueño, Español Sudamericano, Español de España, y Tejas / Spanglish.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(1)',
          popover: { title: '🏷️ Explora por Categoría', description: 'Debajo de los dialectos hay 9 categorías temáticas. Te muestro cada una con sus subcategorías.', side: 'bottom', align: 'center' },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(1)',
          popover: {
            title: '🗣️ Jerga Generacional (Inglés)',
            description: '5 subcategorías: Baby Boomers (1946-1964), Gen X (1965-1979), Gen Y / Millennials (1980-1994), Gen Z (1995-2009), y Gen Alpha (2010-2024).',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(2)',
          popover: {
            title: '🗣️ Jerga Generacional (Español)',
            description: '5 subcategorías: Baby Boomers Español, Gen X Español, Millennials Español, Gen Z Español, y Gen Alpha Español.',
            side: 'bottom',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(3)',
          popover: {
            title: '💬 Interactuar con Otros (Casual — Inglés)',
            description: '6 subcategorías: Citas y Relaciones, Familia y Hogar, Modismos y Expresiones, Sarcasmo y Humor, Social (General), y Mensajes y En línea.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(4)',
          popover: {
            title: '💬 Interactuar con Otros (Casual — Español)',
            description: '6 subcategorías: Citas y Relaciones, Familia y Hogar, Modismos y Expresiones, Sarcasmo y Humor, Social (General), y Mensajes y En línea — todas en español.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(5)',
          popover: {
            title: '💼 Interactuar con Otros (Profesional / Trámites — Inglés)',
            description: '3 subcategorías: Salud, Servicios, y Lugar de Trabajo. Conversaciones de día a día y de trabajo.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(6)',
          popover: {
            title: '💼 Interactuar con Otros (Profesional / Trámites — Español)',
            description: '3 subcategorías: Salud, Servicios, y Lugar de Trabajo — todas en español.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(7)',
          popover: {
            title: '🩺 Terminología Médica',
            description: '10 subcategorías: Jerga Médica Común, Abreviaturas Médicas, Prefijos Médicos, Raíces Médicas, Sufijos Médicos, Jerga Dental, Jerga Óptica, Jerga de Seguros, Jerga Farmacéutica, y Jerga Quirúrgica. Decodifica cualquier cosa que tu médico (o tu seguro) te diga.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(8)',
          popover: {
            title: '🌎 Jerga Regional (Inglés)',
            description: 'Los mismos 15 dialectos regionales del inglés que aparecen arriba — también accesibles como tarjeta de categoría.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="category-chip"]:nth-of-type(9)',
          popover: {
            title: '🌎 Jerga Regional (Español)',
            description: 'Los mismos 13 dialectos regionales del español que aparecen arriba — también accesibles como tarjeta de categoría.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="phrase-card"]',
          popover: {
            title: '🗨️ Tarjetas de frase',
            description: 'Toca cualquier tarjeta para ver el significado real, escucharla, guardarla y compartirla.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="phrase-favorite-btn"]',
          popover: { title: '❤️ Guardar', description: 'Toca el corazón para guardar tus frases favoritas.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="phrase-audio-btn"]',
          popover: { title: '🔊 Escuchar', description: 'Escucha la frase en voz alta — útil para practicar el tono.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-library"]',
          popover: { title: '📚 Biblioteca', description: 'Aquí estás ahora. Tu base para las frases.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-todo"]',
          popover: { title: '✅ Tareas', description: 'Lista de tareas amigable con la neurodiversidad.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-timer"]',
          popover: { title: '⏲️ Temporizador', description: 'Pomodoro con Dino y Llama — enfoque y descansos.', side: 'top', align: 'center' },
        },
        {
          element: '[data-testid="nav-tab-interpreter"]',
          popover: {
            title: '✨ Intérprete de Pensamientos',
            description: 'Escribe tu propio mensaje y Detective Dino lo reescribe en el tono y dialecto regional que elijas — más amable, más claro, y listo para enviar.',
            side: 'top',
            align: 'center',
          },
        },
        {
          element: '[data-testid="nav-tab-friends"]',
          popover: { title: '👥 Amigos', description: 'Conéctate con otras personas. Apóyense mutuamente.', side: 'top', align: 'center' },
        },
        {
          popover: {
            title: '🎉 ¡Listo!',
            description: 'Echa un vistazo. El enlace "Tomar el tour" está en la página Acerca de si quieres repetirlo.',
          },
        },
      ];

      const all = useEs ? stepsEs : stepsEn;
      // Drop steps whose target isn't currently in the DOM — graceful
      // degradation if a category isn't loaded or a testid is renamed.
      const validSteps = all.filter((s) => !s.element || document.querySelector(s.element));
      if (validSteps.length === 0) {
        localStorage.setItem(LIBRARY_TOUR_KEY, '1');
        return;
      }

      const drv = driver({
        // Counter removed — felt longer than it was.
        showProgress: false,
        animate: true,
        smoothScroll: true,
        allowClose: true,
        nextBtnText: useEs ? 'Siguiente →' : 'Next →',
        prevBtnText: useEs ? '← Anterior' : '← Back',
        doneBtnText: useEs ? '¡Listo!' : 'Got it!',
        steps: validSteps,
        onDestroyed: () => {
          localStorage.setItem(LIBRARY_TOUR_KEY, '1');
        },
      });

      fired.current = true;
      drvRef.current = drv;
      drv.drive();
    };

    // Reduced initial delay 800 → 150 ms (user said "significant pause"
    // after the redirect). Polling for the search input + a category
    // chip still gates the start, so we won't fire before the page is
    // ready — we just start CHECKING sooner.
    const initial = setTimeout(() => {
      pollTimer = setInterval(() => {
        if (allReady()) {
          clearInterval(pollTimer);
          if (watchdog) clearTimeout(watchdog);
          startTour();
        }
      }, 150);
    }, 150);

    // Watchdog: still fire after 4s even if some target never mounts —
    // the validSteps filter will drop missing elements gracefully.
    watchdog = setTimeout(() => {
      if (pollTimer) clearInterval(pollTimer);
      startTour();
    }, 4000);

    return () => {
      clearTimeout(initial);
      if (pollTimer) clearInterval(pollTimer);
      if (watchdog) clearTimeout(watchdog);
      // Destroy active tour on route change to avoid React's
      // `removeChild` crash when the user navigates mid-tour.
      if (drvRef.current) {
        try { drvRef.current.destroy(); } catch { /* ignore */ }
        drvRef.current = null;
      }
    };
  }, [pathname, siteIsSpanish]);

  return null;
};

export default LibraryTour;
